import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CUSTOMER_MANAGEMENT_TABS, type CustomerManagementTab } from '@/constants/customers';
import { PLAN_LIMITS } from '@/constants';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { adminAPI } from '@/services/api';
import { Customer } from '@/types';
import { useCustomers, usePlans, useMainDomain, useCustomerActions } from '@/hooks/customers';
import { useViewMode, useDebounce } from '@/hooks/common';
import { filterCustomers, calculateMaxUsers } from '@/utils/customerUtils';
import { handleAPIError } from '@/utils/errorUtils';
import { CustomerCard } from '@/components/users/customer/superadmin/CustomerCard';
import { CustomerTable } from '@/components/users/customer/superadmin/CustomerTable';
import { ServiceConnectionModal } from '@/components/users/customer/superadmin/ServiceConnectionModal';
import { CustomerCreateForm } from '@/components/users/customer/superadmin/forms/CustomerCreateForm';
import { CustomerEditForm } from '@/components/users/customer/superadmin/forms/CustomerEditForm';
import CustomerDashboard from '@/components/users/customer/superadmin/CustomerDashboard';
import ConfirmDialog from '@/components/users/management/common/dialogs/ConfirmDialog';

const CustomerManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const subtabFromUrl = searchParams.get('subtab') as CustomerManagementTab | null;
  
  // URL에서 직접 읽어서 항상 최신 값 사용 (상태 동기화 문제 방지)
  const activeTab: CustomerManagementTab = subtabFromUrl || CUSTOMER_MANAGEMENT_TABS.LIST;
  
  // URL 파라미터 초기화 (한 번만)
  const hasInitializedTab = useRef(false);
  useEffect(() => {
    if (!hasInitializedTab.current && !subtabFromUrl) {
      hasInitializedTab.current = true;
      setSearchParams({ subtab: CUSTOMER_MANAGEMENT_TABS.LIST }, { replace: true });
    }
  }, [subtabFromUrl, setSearchParams]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [originalCustomer, setOriginalCustomer] = useState<Customer | null>(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedCustomerForService, setSelectedCustomerForService] = useState<Customer | null>(null);
  const [availableServices, setAvailableServices] = useState<any[]>([]);
  const [connectedServices, setConnectedServices] = useState<any[]>([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [disconnectConfirm, setDisconnectConfirm] = useState<{ open: boolean; serviceId: number | null }>({ open: false, serviceId: null });
  const [deactivateConfirm, setDeactivateConfirm] = useState<{ open: boolean; customerId: number | null; customerName: string }>({ open: false, customerId: null, customerName: '' });
  const [activateConfirm, setActivateConfirm] = useState<{ open: boolean; customerId: number | null; customerName: string }>({ open: false, customerId: null, customerName: '' });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; customerId: number | null; customerName: string }>({ open: false, customerId: null, customerName: '' });
  
  const plans = usePlans();
  const mainDomain = useMainDomain();
  const { customers, loading, error, fetchCustomers, setError } = useCustomers(includeDeleted);
  const { view, setView } = useViewMode('customerManagement_viewMode', 'card');
  
  const handleRefresh = () => {
    fetchCustomers();
  };
  
  const customerActions = useCustomerActions(handleRefresh);
  
  const [newCustomer, setNewCustomer] = useState<{
    customer_code: string;
    alias: string;
    customer_name: string;
    max_users: number;
    service_url: string;
    contract_end_date: string;
    plan_id: number;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    notes: string;
  }>({
    customer_code: '',
    alias: '',
    customer_name: '',
    max_users: PLAN_LIMITS.PROFESSIONAL.default,
    service_url: '',
    contract_end_date: '',
    plan_id: 1,
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    notes: ''
  });

  useEffect(() => {
    if (customerActions.error) {
      setError(customerActions.error);
    }
  }, [customerActions.error, setError]);

  const fetchAvailableServices = async () => {
    try {
      const response = await adminAPI.getServices('approved');
      setAvailableServices(response.services || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchConnectedServices = async (customerId: number) => {
    try {
      const response = await adminAPI.getCustomerServices(customerId);
      setConnectedServices(response.services || []);
    } catch (error) {
      console.error('Failed to fetch connected services:', error);
      setConnectedServices([]);
    }
  };

  const handleOpenServiceModal = async (customer: Customer) => {
    setSelectedCustomerForService(customer);
    setShowServiceModal(true);
    await fetchAvailableServices();
    await fetchConnectedServices(customer.id);
  };

  const handleConnectService = async (serviceId: number) => {
    if (!selectedCustomerForService) return;

    // availableServices에서 serviceId로 찾아서 service_slug 추출
    const service = availableServices.find((s: any) => s.id === serviceId);
    if (!service || !service.service_slug) {
      setError('서비스를 찾을 수 없습니다.');
      return;
    }

    try {
      await adminAPI.connectServiceToCustomer(selectedCustomerForService.id, service.service_slug);
      await fetchConnectedServices(selectedCustomerForService.id);
      await fetchAvailableServices();
    } catch (error: any) {
      handleAPIError(error, '서비스 연결에 실패했습니다.', setError);
    }
  };

  const handleDisconnectService = (serviceId: number) => {
    setDisconnectConfirm({ open: true, serviceId });
  };

  const confirmDisconnectService = async () => {
    if (!selectedCustomerForService || !disconnectConfirm.serviceId) return;

    // connectedServices에서 serviceId로 찾아서 service_slug 추출
    const service = connectedServices.find((s: any) => s.id === disconnectConfirm.serviceId);
    if (!service || !service.service_slug) {
      setError('서비스를 찾을 수 없습니다.');
      setDisconnectConfirm({ open: false, serviceId: null });
      return;
    }

    try {
      await adminAPI.disconnectServiceFromCustomer(selectedCustomerForService.id, service.service_slug);
      await fetchConnectedServices(selectedCustomerForService.id);
      setDisconnectConfirm({ open: false, serviceId: null });
    } catch (error: any) {
      handleAPIError(error, '서비스 연결 해제에 실패했습니다.', setError);
      setDisconnectConfirm({ open: false, serviceId: null });
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    const plan = plans.find(p => p.id === newCustomer.plan_id);
    const planName = plan?.plan_name.toLowerCase();
    const maxUsers = calculateMaxUsers(planName, newCustomer.max_users);
    
    const customerData: any = {
      ...newCustomer,
      max_users: maxUsers
    };
    
    delete customerData.service_url;
    
    if (!customerData.customer_code) customerData.customer_code = undefined;
    if (!customerData.alias) customerData.alias = undefined;
    if (!customerData.contact_name) customerData.contact_name = undefined;
    if (!customerData.contact_email) customerData.contact_email = undefined;
    if (!customerData.contact_phone) customerData.contact_phone = undefined;
    if (!customerData.notes) customerData.notes = undefined;
    
    try {
      await customerActions.createCustomer(customerData);
      setShowCreateForm(false);
      setNewCustomer({
        customer_code: '',
        alias: '',
        customer_name: '',
        max_users: PLAN_LIMITS.PROFESSIONAL.default,
        service_url: '',
        contract_end_date: '',
        plan_id: 1,
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        notes: ''
      });
    } catch (error) {
      // Error is handled by hook
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer({ ...customer });
    setOriginalCustomer({ ...customer });
  };

  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomer || !originalCustomer) return;

    const updatedData: Partial<Customer> = {};
    for (const key in editingCustomer) {
      const field = key as keyof Customer;
      if (editingCustomer[field] !== originalCustomer[field]) {
        (updatedData as any)[field] = editingCustomer[field];
      }
    }

    if (editingCustomer.plan?.id) {
      (updatedData as any).plan_id = editingCustomer.plan.id;
    }

    delete updatedData.service_url;

    try {
      await customerActions.updateCustomer(editingCustomer.id, updatedData);
      setEditingCustomer(null);
      setOriginalCustomer(null);
    } catch (error) {
      // Error is handled by hook
    }
  };

  const handleSubTabChange = (tab: string) => {
    const validTab = tab as CustomerManagementTab;
    // 즉시 URL 업데이트 (상태는 URL에서 자동으로 읽어옴)
    setSearchParams({ subtab: validTab }, { replace: true });
  };

  const filteredCustomers = filterCustomers(customers, debouncedSearchQuery);

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" message="고객사 목록을 불러오는 중..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">고객사 관리</h2>
          <p className="text-sm text-gray-500 mt-1">고객사 정보를 관리하고 서비스를 연결합니다</p>
        </div>
        <div className="flex items-center space-x-2">
          {activeTab === CUSTOMER_MANAGEMENT_TABS.LIST && (
            <Button onClick={() => setShowCreateForm(true)}>
              새 고객사 추가
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <LoadingSpinner size="sm" />
                <span>새로고침 중...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>상태 새로고침</span>
              </div>
            )}
          </Button>
          {activeTab === CUSTOMER_MANAGEMENT_TABS.LIST && (
            <ViewToggle view={view} onViewChange={setView} />
          )}
        </div>
      </div>

      {error && (
        <AlertMessage 
          type="error" 
          message={error} 
          onClose={() => setError('')} 
        />
      )}

      <Tabs value={activeTab} onValueChange={handleSubTabChange}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value={CUSTOMER_MANAGEMENT_TABS.LIST}>고객사 목록</TabsTrigger>
          <TabsTrigger value={CUSTOMER_MANAGEMENT_TABS.DASHBOARD}>고객사 대시보드</TabsTrigger>
        </TabsList>

        <TabsContent value={CUSTOMER_MANAGEMENT_TABS.LIST} className="space-y-4">
          <div className="flex items-center justify-between">
            <Input
              type="text"
              placeholder="회사명, 고객사 코드로 검색..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="max-w-xs"
            />
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="includeDeleted"
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="includeDeleted" className="text-sm text-gray-700">
                삭제된 고객사 포함
              </label>
            </div>
          </div>

          <CustomerCreateForm
            open={showCreateForm}
            newCustomer={newCustomer}
            plans={plans}
            mainDomain={mainDomain}
            loading={customerActions.loading}
            onCustomerChange={setNewCustomer}
            onSubmit={handleCreateCustomer}
            onCancel={() => setShowCreateForm(false)}
          />

          {editingCustomer && originalCustomer && (
            <CustomerEditForm
              open={!!editingCustomer}
              editingCustomer={editingCustomer}
              originalCustomer={originalCustomer}
              plans={plans}
              mainDomain={mainDomain}
              loading={customerActions.loading}
              onCustomerChange={setEditingCustomer}
              onSubmit={handleUpdateCustomer}
              onCancel={() => {
                setEditingCustomer(null);
                setOriginalCustomer(null);
              }}
            />
          )}

          {filteredCustomers.length === 0 && !loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">등록된 고객사가 없습니다.</p>
              <Button onClick={() => setShowCreateForm(true)}>
                첫 번째 고객사 추가하기
              </Button>
            </div>
          ) : view === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCustomers.map((customer) => (
                <CustomerCard
                  key={customer.id}
                  customer={customer}
                  editingCustomerId={editingCustomer?.id}
                  onEdit={handleEditCustomer}
                  onServiceConnection={handleOpenServiceModal}
                  onDeactivate={(customerId) => {
                    const customer = customers.find(c => c.id === customerId);
                    setDeactivateConfirm({ open: true, customerId, customerName: customer?.customer_name || '' });
                  }}
                  onActivate={(customerId) => {
                    const customer = customers.find(c => c.id === customerId);
                    setActivateConfirm({ open: true, customerId, customerName: customer?.customer_name || '' });
                  }}
                  onDelete={(customerId) => {
                    const customer = customers.find(c => c.id === customerId);
                    setDeleteConfirm({ open: true, customerId, customerName: customer?.customer_name || '' });
                  }}
                />
              ))}
            </div>
          ) : (
            <CustomerTable
              customers={filteredCustomers}
              onEdit={handleEditCustomer}
              onServiceConnection={handleOpenServiceModal}
              onDeactivate={(customerId) => {
                const customer = customers.find(c => c.id === customerId);
                setDeactivateConfirm({ open: true, customerId, customerName: customer?.customer_name || '' });
              }}
              onActivate={(customerId) => {
                const customer = customers.find(c => c.id === customerId);
                setActivateConfirm({ open: true, customerId, customerName: customer?.customer_name || '' });
              }}
              onDelete={(customerId) => {
                const customer = customers.find(c => c.id === customerId);
                setDeleteConfirm({ open: true, customerId, customerName: customer?.customer_name || '' });
              }}
            />
          )}
        </TabsContent>

        <TabsContent value={CUSTOMER_MANAGEMENT_TABS.DASHBOARD} className="space-y-4">
          <CustomerDashboard
            customerId={selectedCustomerId}
            onSelectCustomer={(customerId) => {
              setSelectedCustomerId(customerId);
              if (customerId) {
                setSearchParams({ subtab: CUSTOMER_MANAGEMENT_TABS.DASHBOARD, customerId: customerId.toString() });
              } else {
                setSearchParams({ subtab: CUSTOMER_MANAGEMENT_TABS.DASHBOARD });
              }
            }}
            customers={customers.map(c => ({ id: c.id, customer_name: c.customer_name, customer_code: c.customer_code }))}
          />
        </TabsContent>
      </Tabs>

      {showServiceModal && selectedCustomerForService && (
        <ServiceConnectionModal
          customer={selectedCustomerForService}
          availableServices={availableServices}
          connectedServices={connectedServices}
          onConnect={handleConnectService}
          onDisconnect={handleDisconnectService}
          onClose={() => {
            setShowServiceModal(false);
            setSelectedCustomerForService(null);
          }}
        />
      )}

      <ConfirmDialog
        open={disconnectConfirm.open}
        title="서비스 연결 해제"
        description="이 서비스 연결을 해제하시겠습니까?"
        confirmText="해제"
        cancelText="취소"
        variant="default"
        onConfirm={confirmDisconnectService}
        onCancel={() => setDisconnectConfirm({ open: false, serviceId: null })}
      />

      <ConfirmDialog
        open={activateConfirm.open}
        title="고객사 활성화"
        description={`"${activateConfirm.customerName}" 고객사를 활성화하시겠습니까?\n\n활성화된 고객사는 모든 사용자가 로그인할 수 있습니다.`}
        confirmText="활성화"
        cancelText="취소"
        variant="default"
        onConfirm={async () => {
          if (!activateConfirm.customerId) return;
          try {
            await customerActions.activateCustomer(activateConfirm.customerId);
            setActivateConfirm({ open: false, customerId: null, customerName: '' });
          } catch (error) {
            // Error is handled by hook
            setActivateConfirm({ open: false, customerId: null, customerName: '' });
          }
        }}
        onCancel={() => setActivateConfirm({ open: false, customerId: null, customerName: '' })}
      />

      <ConfirmDialog
        open={deactivateConfirm.open}
        title="고객사 비활성화"
        description={`"${deactivateConfirm.customerName}" 고객사를 비활성화하시겠습니까?\n\n비활성화된 고객사는 기업 관리자만 로그인할 수 있습니다.`}
        confirmText="비활성화"
        cancelText="취소"
        variant="default"
        onConfirm={async () => {
          if (!deactivateConfirm.customerId) return;
          try {
            await customerActions.deactivateCustomer(deactivateConfirm.customerId);
            setDeactivateConfirm({ open: false, customerId: null, customerName: '' });
          } catch (error) {
            // Error is handled by hook
            setDeactivateConfirm({ open: false, customerId: null, customerName: '' });
          }
        }}
        onCancel={() => setDeactivateConfirm({ open: false, customerId: null, customerName: '' })}
      />

      <ConfirmDialog
        open={deleteConfirm.open}
        title="고객사 삭제"
        description={`정말로 "${deleteConfirm.customerName}" 고객사를 삭제하시겠습니까?\n\n삭제된 고객사는 복구할 수 없습니다.`}
        confirmText="삭제"
        cancelText="취소"
        variant="destructive"
        onConfirm={async () => {
          if (!deleteConfirm.customerId) return;
          try {
            await customerActions.deleteCustomer(deleteConfirm.customerId);
            setDeleteConfirm({ open: false, customerId: null, customerName: '' });
          } catch (error) {
            // Error is handled by hook
            setDeleteConfirm({ open: false, customerId: null, customerName: '' });
          }
        }}
        onCancel={() => setDeleteConfirm({ open: false, customerId: null, customerName: '' })}
      />
    </div>
  );
};

export default CustomerManagement;

